import type { HealthcareService } from '@medplum/fhirtypes';
import { useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Stack,
  useToast,
} from '@eluve/components';
import {
  FhirEditScope,
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
  useFhirDraft,
  useFhirEditScope,
} from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

/**
 * The add path is the edit path over a draft: the dialog owns its own
 * FhirEditScope, seeds a bare HealthcareService, and the renderer
 * dispatches the same edit variant it uses for an existing resource.
 * Commit sees a draft and creates instead of patching.
 */

const CreateFooter: React.FC<{ onDone: (created: boolean) => void }> = ({
  onDone,
}) => {
  const { dirtyCount, commit, reset } = useFhirEditScope();
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handleCreate = async () => {
    setSaving(true);
    try {
      const result = await commit();
      if (result.ok) {
        toast.success('Service created.');
        onDone(true);
      } else {
        toast.error(
          `Couldn't save: ${result.failures[0]?.error.message ?? 'unknown error'}`,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack direction="horizontal" gap={2} justify="end" className="pt-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          reset();
          onDone(false);
        }}
      >
        Cancel
      </Button>
      <Button
        size="sm"
        disabled={dirtyCount === 0 || saving}
        onClick={() => void handleCreate()}
        data-testid="create-service"
      >
        {saving ? 'Creating…' : 'Create service'}
      </Button>
    </Stack>
  );
};

const DraftServiceForm: React.FC = () => {
  const draft = useFhirDraft<HealthcareService>(() => ({
    resourceType: 'HealthcareService',
    active: true,
  }));
  return <RenderResource resource={draft} />;
};

const AddServiceBody: React.FC<{ onDone: (created: boolean) => void }> = ({
  onDone,
}) => {
  const { client } = useMedplum();
  const map = useMemo(() => buildResolutionMap([]), []);

  return (
    <FhirEditScope active transport={client ?? undefined}>
      <FhirResourceScope map={map} placement="modal">
        <DraftServiceForm />
      </FhirResourceScope>
      <CreateFooter onDone={onDone} />
    </FhirEditScope>
  );
};

export const AddServiceDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}> = ({ open, onOpenChange, onCreated }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent size="lg">
      <DialogHeader>
        <DialogTitle>New service</DialogTitle>
        <DialogDescription>
          A session type patients can book. Duration and slot spacing decide
          which start times the booking calendar shows.
        </DialogDescription>
      </DialogHeader>
      {open && (
        <AddServiceBody
          onDone={(created) => {
            onOpenChange(false);
            if (created) onCreated();
          }}
        />
      )}
    </DialogContent>
  </Dialog>
);
